import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "../../../components/navigation/BackButton";
import type { CustomerItem } from "../../../mocks/customers";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { usePayments } from "../../../hooks/usePayments";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { readSessionUser } from "../../../lib/authSession";
import {
  applyPaymentToSelectedShares,
  findCustomerByShare,
  type PaymentFormPayload,
} from "../../../lib/salesFlow";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../lib/animalLabels";
import { getShareTotal } from "../../../lib/cascadeOps";
import { getReportPrintSettings } from "../../../lib/reportPrintSettings";
import { openPaymentPrintWindow } from "../../../lib/printTemplates";
import CustomerSearchBox from "./components/CustomerSearchBox";
import BankImportModal from "./components/BankImportModal";
import ShareSelectorList, {
  getCustomerShares,
  type ShareWithAnimal,
} from "./components/ShareSelectorList";
import PaymentFormPanel from "./components/PaymentFormPanel";
import PaymentSuccess from "./components/PaymentSuccess";
import { rehydrateAnimals } from "../../../store/animalsStore";
import { rehydrateCustomers } from "../../../store/customersStore";
import { rehydratePayments } from "../../../store/paymentsStore";
import { applyOperationResult } from "../../../lib/operationRefresh";
import { createPaymentOperationRequest } from "../../../lib/api";

type Step = "search" | "select" | "form" | "success";

interface PaymentRouteState {
  customerId?: number;
  selectedShareIds?: number[];
}

function fmt(n: number) {
  return n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

export default function OdemeAlPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { animals, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();
  const { transfers: bankTransfers, setAllBankTransfers } = useBankTransfers();

  const [step, setStep] = useState<Step>("search");
  const [customer, setCustomer] = useState<CustomerItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [lastPayment, setLastPayment] = useState<PaymentFormPayload | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importMessage, setImportMessage] = useState<string>("");

  useEffect(() => {
    const routeState = (location.state as PaymentRouteState | null) ?? null;
    if (!routeState) return;
    if (customers.length === 0 || animals.length === 0) return;

    let nextCustomer: CustomerItem | null = null;

    if (routeState.customerId) {
      nextCustomer =
        customers.find((item) => item.id === routeState.customerId) ?? null;
    }

    if (!nextCustomer && routeState.selectedShareIds?.length) {
      const firstShare = animals
        .flatMap((animal) => animal.hisseler)
        .find((share) => routeState.selectedShareIds?.includes(share.id));

      if (firstShare) {
        nextCustomer = findCustomerByShare(customers, firstShare);
      }
    }

    if (nextCustomer) {
      setCustomer(nextCustomer);
    }

    if (routeState.selectedShareIds?.length) {
      setSelectedIds(routeState.selectedShareIds);
    }

    if (nextCustomer && routeState.selectedShareIds?.length) {
      setStep("form");
    } else if (nextCustomer) {
      setStep("select");
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [animals, customers, location.pathname, location.state, navigate]);

  function handleSelectCustomer(nextCustomer: CustomerItem) {
    setCustomer(nextCustomer);
    setSelectedIds([]);
    setLastPayment(null);
    setStep("select");
  }

  function handleClearCustomer() {
    setCustomer(null);
    setSelectedIds([]);
    setLastPayment(null);
    setStep("search");
  }

  function handleToggle(shareId: number) {
    setSelectedIds((prev) =>
      prev.includes(shareId)
        ? prev.filter((id) => id !== shareId)
        : [...prev, shareId]
    );
  }

  function handleSelectAll(ids: number[]) {
    setSelectedIds(ids);
  }

  function handleProceedToForm() {
    if (selectedIds.length === 0) return;
    setStep("form");
  }

  const allShares = customer ? getCustomerShares(customer) : [];

  const selectedShares: ShareWithAnimal[] = allShares.filter((item) =>
    selectedIds.includes(item.share.id)
  );

  const maxKalan = useMemo(() => {
    return selectedShares.reduce(
      (sum, item) => sum + Math.max(0, item.share.kalan),
      0
    );
  }, [selectedShares]);

  const selectedAnimalsSummary = useMemo(() => {
    return Array.from(
      new Map(
        selectedShares.map((item) => [
          item.animal.id,
          {
            animalId: item.animal.id,
            kupeNo: getDisplayKupeNo(item.animal),
            siparisNo: getDisplaySiparisNo(item.animal),
            animalType: item.animal.tur === "Büyükbaş" ? "B.Baş" : "K.Baş",
          },
        ])
      ).values()
    );
  }, [selectedShares]);

  async function handleSubmitPayment(data: PaymentFormPayload, linkedBankTransferIds: string[] = []) {
    if (!customer) return;
    if (selectedShares.length === 0) return;

    const previousPaid = selectedShares.reduce((sum, item) => sum + Number(item.share.odenen || 0), 0);
    const totalSale = selectedShares.reduce((sum, item) => sum + getShareTotal(item.share), 0);

    let result = applyPaymentToSelectedShares({
      animals,
      customers,
      payments,
      customer,
      selectedShares,
      data,
    });

    const sessionUser = readSessionUser();

    if (sessionUser?.rol === "Admin") {
      let nextAnimals = result.animals;

      for (const animalId of result.completedAnimalIds) {
        const animal = nextAnimals.find((item) => item.id === animalId);
        if (!animal || animal.kesimeUygun) continue;

        const ok = window.confirm(
          `${getDisplayKupeNo(animal)} ödemesi tamamlandı.\n\nKesime gönderilsin mi?`
        );

        if (ok) {
          nextAnimals = nextAnimals.map((item) =>
            item.id === animalId ? { ...item, kesimeUygun: true } : item
          );
        }
      }

      result = {
        ...result,
        animals: nextAnimals,
      };
    }

    const markKesimeUygunAnimalIds = result.animals
      .filter((nextAnimal) => {
        const beforeAnimal = animals.find((item) => item.id === nextAnimal.id);
        return Boolean(beforeAnimal && !beforeAnimal.kesimeUygun && nextAnimal.kesimeUygun);
      })
      .map((item) => item.id);

    try {
      const operationResult = await createPaymentOperationRequest({
        customerId: customer.id,
        shareIds: selectedShares.map((item) => item.share.id),
        data,
        linkedBankTransferIds,
        markKesimeUygunAnimalIds,
      });
      await applyOperationResult(operationResult);

      const printSettings = getReportPrintSettings();
      if (printSettings.paymentAutoPrint) {
        const first = selectedShares[0];
        const hisseLabel = selectedShares.map((item) => item.share.hisseNo).join(", ");
        const totalPaid = previousPaid + data.tutar;
        openPaymentPrintWindow({
          tarih: data.tarih,
          fisNo: data.fisNo || data.havaleNo,
          kupeNo: first ? getDisplayKupeNo(first.animal) : "—",
          siparisNo: first ? getDisplaySiparisNo(first.animal) : "—",
          customerName: customer.adSoyad,
          hisseLabel,
          totalSale,
          previousPaid,
          paymentAmount: data.tutar,
          totalPaid,
          remainingAmount: totalSale - totalPaid,
          paymentType: data.tip,
          animalType: first ? (first.animal.tur === "Büyükbaş" ? "B.Baş" : "K.Baş") : "—",
          operationalNote: data.aciklama,
        }, printSettings);
      }

      setLastPayment(data);
      setStep("success");
    } catch (error) {
      console.error(error);
      window.alert("Ödeme kaydı sırasında çakışma veya bağlantı hatası oluştu. Lütfen son veriyi kontrol edip tekrar deneyin.");
    }
  }

  function handleNewPayment() {
    setCustomer(null);
    setSelectedIds([]);
    setLastPayment(null);
    setStep("search");
  }

  const stepLabels: { key: Step; label: string }[] = [
    { key: "search", label: "Müşteri" },
    { key: "select", label: "Hisse" },
    { key: "form", label: "Ödeme" },
    { key: "success", label: "Onay" },
  ];

  const stepIndex = stepLabels.findIndex((item) => item.key === step);

  async function handleImportApplied(summary: { importedRows: number; importedAmount: number }) {
    await Promise.all([rehydrateAnimals(), rehydrateCustomers(), rehydratePayments()]);
    setCustomer(null);
    setSelectedIds([]);
    setLastPayment(null);
    setStep("search");
    setImportMessage(`${summary.importedRows} satır içeri alındı • ${fmt(summary.importedAmount)}`);
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-5xl mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="bg-white border border-farm-border rounded-farm px-4 py-3">
          {importMessage && (
            <div className="mb-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {importMessage}
            </div>
          )}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-heading font-bold text-base text-farm-brown">
                Ödeme Al
              </h1>
              <p className="text-xs text-farm-muted mt-1">
                Hayvan detayından veya müşteri detayından gelen tüm ödeme akışları
                burada aynı ekranla çalışır.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <BackButton />
              <button
                type="button"
                onClick={() => setIsImportOpen(true)}
                className="h-9 px-3 rounded-xl border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth"
              >
                Excel ile Havale Yükle
              </button>
              {stepLabels.map((item, index) => (
                <div key={item.key} className="flex items-center gap-2">
                  <div
                    className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-bold ${
                      index < stepIndex
                        ? "bg-farm-earth border-farm-earth text-white"
                        : index === stepIndex
                        ? "border-farm-earth text-farm-earth bg-white"
                        : "border-farm-border text-farm-muted bg-white"
                    }`}
                  >
                    {index < stepIndex ? (
                      <i className="ri-check-line text-xs" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span
                    className={`text-xs font-semibold ${
                      index <= stepIndex ? "text-farm-brown" : "text-farm-muted"
                    }`}
                  >
                    {item.label}
                  </span>
                  {index < stepLabels.length - 1 && (
                    <div className="w-5 h-px bg-farm-border" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {step !== "success" && (
          <div className="bg-white border border-farm-border rounded-farm p-4">
            <p className="text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-3">
              Müşteri Seç
            </p>

            <CustomerSearchBox
              onSelect={handleSelectCustomer}
              selectedCustomer={customer}
              onClear={handleClearCustomer}
            />
          </div>
        )}

        {(step === "select" || step === "form") && customer && (
          <div className="bg-white border border-farm-border rounded-farm p-4 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] font-semibold text-farm-muted uppercase tracking-wide">
                  Seçili Müşteri
                </p>
                <p className="text-sm font-semibold text-farm-brown mt-1">
                  {customer.adSoyad}
                </p>
                <p className="text-xs text-farm-muted mt-0.5">{customer.telefon}</p>
              </div>

              {selectedShares.length > 0 && (
                <div className="text-right">
                  <p className="text-[10px] font-semibold text-farm-muted uppercase tracking-wide">
                    Seçili Kalan
                  </p>
                  <p className="text-base font-bold text-farm-earth mt-1">
                    {fmt(maxKalan)}
                  </p>
                </div>
              )}
            </div>

            <div>
              <p className="text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-3">
                Hisse Seç
              </p>

              <ShareSelectorList
                customer={customer}
                selected={selectedIds}
                onToggle={handleToggle}
                onSelectAll={handleSelectAll}
              />

              {step === "select" && (
                <button
                  type="button"
                  onClick={handleProceedToForm}
                  disabled={selectedIds.length === 0}
                  className="mt-4 w-full h-11 rounded-full bg-farm-earth text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ödeme Formuna Geç
                </button>
              )}

              {step === "form" && (
                <button
                  type="button"
                  onClick={() => setStep("select")}
                  className="mt-2 w-full py-2 text-xs text-farm-muted hover:text-farm-brown transition-colors"
                >
                  ← Hisse seçimini değiştir
                </button>
              )}
            </div>
          </div>
        )}

        {step === "form" && customer && (
          <div className="bg-white border border-farm-border rounded-farm p-4 space-y-4">
            <div>
              <p className="text-[10px] font-semibold text-farm-muted uppercase tracking-wide">
                Ödeme Bilgileri
              </p>
              <p className="text-xs text-farm-muted mt-1">
                Seçilen hisseler için ödeme kaydı oluşturulur. Tarih değiştirilebilir.
              </p>
            </div>

            {selectedAnimalsSummary.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {selectedAnimalsSummary.map((item) => (
                  <div
                    key={item.animalId}
                    className="rounded-xl border border-farm-border bg-farm-warm px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] text-farm-muted uppercase tracking-wide">
                        Küpe No
                      </div>
                      <span className="rounded-full bg-farm-earth-pale px-1.5 py-0.5 text-[10px] font-bold text-farm-earth">
                        {item.animalType}
                      </span>
                    </div>
                    <div className="text-sm font-bold text-farm-brown mt-0.5">
                      {item.kupeNo}
                    </div>
                    <div className="text-[11px] text-farm-muted mt-1">
                      Sipariş No:{" "}
                      <span className="font-semibold text-farm-brown">
                        {item.siparisNo}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <PaymentFormPanel
              customer={customer}
              selectedShares={selectedShares}
              maxKalan={maxKalan}
              onSubmit={handleSubmitPayment}
              bankTransfers={bankTransfers}
            />
          </div>
        )}

        {step === "success" && customer && lastPayment && (
          <div className="bg-white border border-farm-border rounded-farm p-6">
            <PaymentSuccess
              customer={customer}
              selectedShares={selectedShares}
              payment={lastPayment}
              onNewPayment={handleNewPayment}
            />
          </div>
        )}

        {step === "search" && (
          <div className="bg-white border border-dashed border-farm-border rounded-farm p-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-farm-muted">
              <i className="ri-search-2-line text-4xl" />
            </div>
            <p className="text-sm text-farm-brown font-medium">Müşteri arayın</p>
            <p className="text-xs text-farm-muted mt-1">
              Telefon veya isim ile müşteri seçin, ardından hisse ve ödeme ekleyin.
            </p>
          </div>
        )}
        <BankImportModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onApplied={handleImportApplied}
        />
      </div>
    </div>
  );
}